/* ============================================================
   storage — streaming copies of a video.

   The server makes lighter copies of heavy videos in the background
   (see "Streaming copies" in server/nas.py): 1080p at ~8 Mb/s and
   720p at ~3.5 Mb/s, next to an original that may be 130 Mb/s. This
   asks which exist, keeps asking while one is being made, and turns
   the answer into the player's quality list.

   Used by the signed-in viewer and by view-only public links alike —
   only the URLs differ, so they are passed in.
   ============================================================ */

import { useEffect, useState } from 'react';

const mbps = (bps) => (bps ? `${(bps / 1e6).toFixed(bps >= 1e7 ? 0 : 1)} Mb/s` : '');

/**
 * Poll a video's streaming copies while they are being made.
 * @param {() => Promise<object>|null} fetchStatus  null when the file is not a video
 */
export function useStreams(fetchStatus, key) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    setInfo(null);
    if (!fetchStatus) return undefined;
    let dead = false;
    let t = 0;
    const tick = async () => {
      try {
        const s = await fetchStatus();
        if (dead) return;
        setInfo(s);
        // Keep asking only while something is on its way.
        if (s && (s.status === 'queued' || s.status === 'working')) t = setTimeout(tick, 5000);
      } catch {
        if (!dead) t = setTimeout(tick, 15000);
      }
    };
    tick();
    return () => { dead = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return info;
}

/**
 * The player's quality list: the copies that are ready, then the original.
 * `original` may be null (a view link whose original is not offered).
 */
export function qualitiesFrom(info, copyUrl, original) {
  const out = [];
  if (info?.status === 'ready') {
    for (const r of info.renditions || []) {
      out.push({ id: r.q, label: `${r.q}p`, note: mbps(r.bitrate), bitrate: r.bitrate || 0, src: copyUrl(r.q) });
    }
  }
  if (original) {
    const b = info?.source?.bitrate;
    out.push({ id: 'original', label: 'Original', note: mbps(b), bitrate: b || Infinity, src: original });
  }
  return out;
}

/** What to say about copies that are not ready yet. */
export function streamNote(info) {
  if (!info) return null;
  if (info.status === 'working') return `Making a smoother copy — ${Math.round((info.progress || 0) * 100)}%`;
  if (info.status === 'queued' || info.status === 'evicted') return 'A smoother copy is queued';
  return null;
}
